import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Flex, FormControl, FormLabel } from '@chakra-ui/react';

import {
  DateInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  InputWithConfirm,
  AddressInputWithConfirm,
  PhoneNumberInputWithConfirm,
  CardContainer,
  CardWithModal,
  RadioOptionsWithConfirm,
  IQuestion,
  Select,
  isValidDate,
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import { isFutureDate } from '../../../../../utilities/validation';
import FamilyMembers from './familyMembers';
import { IDate, IFamilyMember, IPhone } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import isemail from 'isemail';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

const VisaPrepFormArrivalPage1 = (_props: any) => {
    const { t } = useTranslation('common');
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE5));
  };

  const onChange = (fieldName: string, value: any) => dispatch(createUpdateVPIFieldAction(fieldName, value));
  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const visaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);
  const {
    hasArrivalDate,
    arrivalDate: arrivalToUkDate,
    lengthOfStayDays,
    lengthOfStayMonths,
    lengthOfStayYears,
    ukPresence,
    ukFamily,
    stayingWithFamily,
    familyMembers = [],
    familyContactAddress,
    familyContactCity,
    familyContactPostcode,
    familyContactCountry,
    hasOtherContactAddress,
    otherContactAddress,
    otherContactCity,
    otherContactPostcode,
    otherContactCountry,
    familyEmail,
  } = visaPrepForm;

  const familyPhoneNumber = useMemo(
    () => ({
      code: visaPrepForm.familyPhoneNumber ? visaPrepForm.familyPhoneNumber.split('-')[0] : null,
      phone: visaPrepForm.familyPhoneNumber ? visaPrepForm.familyPhoneNumber.split('-')[1] : null,
    }),
    [visaPrepForm.familyPhoneNumber],
  );

  const otherPhoneNumber = useMemo(
    () => ({
      code: visaPrepForm.otherPhoneNumber ? visaPrepForm.otherPhoneNumber.split('-')[0] : null,
      phone: visaPrepForm.otherPhoneNumber ? visaPrepForm.otherPhoneNumber.split('-')[1] : null,
    }),
    [visaPrepForm.otherPhoneNumber],
  );

  // constants
  const currentStep = 1;
  const steps = 1;

  // component state
  const [complete, setComplete] = useState(false);
  const [arrivalDate, setArrivalDate] = useState<IDate>(() => getDateFromString(arrivalToUkDate));
  const [arrivalDateError, setArrivalDateError] = useState('');
  const [familyPhone, setFamilyPhone] = useState<IPhone>();
  const [isFamilyPhoneValid, setFamilyPhoneValid] = useState(true);
  const [otherPhone, setOtherPhone] = useState<IPhone>();
  const [isOtherPhoneValid, setOtherPhoneValid] = useState(true);
  const [familyMembersState, setFamilyMembersState] = useState<IFamilyMember[]>([]);
  const [familyEmailInput, setFamilyEmailInput] = useState(familyEmail);
  const [familyEmailInputError, setFamilyEmailInputError] = useState('');
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setFamilyMembersState([
      ...familyMembers,
      {
        nationality: '',
        familyName: '',
        givenName: '',
        dob: '',
      },
    ]);
    setShowModal([...familyMembers.map(_ => false), false]);
  }, [familyMembers]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteMember = (index: number): void => {
    onChange(VQ.FAMILY_MEMBERS, [...familyMembers.slice(0, index), ...familyMembers.slice(index + 1)]);
  };

  useEffect(() => {
    if (isFamilyPhoneValid) {
      setFamilyPhone(familyPhoneNumber);
    }
  }, [isFamilyPhoneValid, familyPhoneNumber]);

  useEffect(() => {
    if (isOtherPhoneValid) {
      setOtherPhone(otherPhoneNumber);
    }
  }, [isOtherPhoneValid, otherPhoneNumber]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasArrivalDate,
      arrivalDateError,
      familyEmailInputError,
      isFamilyPhoneValid,
      isOtherPhoneValid,
      arrivalDate: arrivalToUkDate,
      familyContactAddress,
      familyContactCity,
      familyContactCountry,
      familyContactPostcode,
      familyPhoneNumber,
      familyMembers,
      hasOtherContactAddress,
      lengthOfStayDays,
      lengthOfStayMonths,
      lengthOfStayYears,
      otherContactAddress,
      otherContactCity,
      otherContactCountry,
      otherContactPostcode,
      otherPhoneNumber,
      stayingWithFamily,
      ukFamily,
      ukPresence,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasArrivalDate,
    arrivalDateError,
    isFamilyPhoneValid,
    isOtherPhoneValid,
    arrivalToUkDate,
    familyContactAddress,
    familyContactCity,
    familyContactCountry,
    familyContactPostcode,
    familyPhoneNumber,
    familyMembers,
    hasOtherContactAddress,
    lengthOfStayDays,
    lengthOfStayMonths,
    lengthOfStayYears,
    otherContactAddress,
    otherContactCity,
    otherContactCountry,
    otherContactPostcode,
    otherPhoneNumber,
    stayingWithFamily,
    ukFamily,
    ukPresence,
    familyEmailInputError,
  ]);

  const validateDate = (_dateType: string, dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      const isFuture = isFutureDate(dateValue);
      if (!isValid) {
        setArrivalDateError(`Doesn't look like a valid date`);
      } else if (!isFuture) {
        setArrivalDateError(`Date must be in the future`);
      } else {
        onChange(VQ.ARRIVAL_DATE, getStringFromDate(dateValue));
        setArrivalDateError('');
      }
    }
  };

  const validateEmail = () => {
    const isValidEmail = isemail.validate(familyEmailInput ?? '');

    if (isValidEmail) {
      setFamilyEmailInputError('');
    } else {
      setFamilyEmailInputError('Please enter a valid email address');
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Arrival'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_ARRIVAL_DATE}
        id={VQ.HAS_ARRIVAL_DATE}
        label={t('common:doYouKnowYourPlannedArrivalDateI')}
        onClick={(question: IQuestion) => onChange(VQ.HAS_ARRIVAL_DATE, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasArrivalDate === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasArrivalDate === false,
          },
        ]}
      />

      {hasArrivalDate === true ? (
        <DateInputWithConfirm
          key={VQ.ARRIVAL_DATE}
          id={VQ.ARRIVAL_DATE}
          label={t('common:dateOfPlannedArrivalInTheUk')}
          value={arrivalDate}
          errorText={arrivalDateError}
          onChangeDD={(evt: any) => {
            let arrDate: IDate = { ...arrivalDate };
            arrDate = {
              ...arrDate,
              DD: evt.currentTarget.value,
            };
            setArrivalDate(arrDate);
          }}
          onChangeMM={(evt: any) => {
            let arrDate: IDate = { ...arrivalDate };
            arrDate = {
              ...arrDate,
              MM: evt.currentTarget.value,
            };
            setArrivalDate(arrDate);
          }}
          onChangeYY={(evt: any) => {
            const { value } = evt.currentTarget;
            if (value.length === 4) {
              let arrDate: IDate = { ...arrivalDate };
              arrDate = {
                ...arrDate,
                YYYY: value,
              };
              setArrivalDate(arrDate);

              validateDate('arrivalDate', {
                ...arrDate,
                YYYY: value,
              });
            } else {
              let arrDate: IDate = { ...arrivalDate };
              arrDate = {
                ...arrDate,
                YYYY: value,
              };
              setArrivalDate(arrDate);
            }
          }}
          onBlur={(_evt: any) =>
            validateDate('arrivalDate', {
              ...arrivalDate,
            })
          }
        />
      ) : null}

      {hasArrivalDate === true ? (
        <>
          <FormControl mb={10}>
            <FormLabel as="legend" fontSize="lg">
              {t('common:howLongDoYouIntendToStayInTheUk')}
                                      </FormLabel>

            <Flex gap={3}>
              <Select
                key={VQ.LENGTH_IN_UK_INTENTION_DD}
                id={VQ.LENGTH_IN_UK_INTENTION_DD}
                label={t('common:days')}
                options={[...Array(32)].map((_, index: number) => ({
                  label: `${index}`,
                  value: `${index}`,
                }))}
                value={lengthOfStayDays}
                onChange={(evt: any) => onChange(VQ.LENGTH_IN_UK_INTENTION_DD, Number(evt.currentTarget.value))}
              />

              <Select
                key={VQ.LENGTH_IN_UK_INTENTION_MM}
                id={VQ.LENGTH_IN_UK_INTENTION_MM}
                label={t('common:months')}
                options={[...Array(13)].map((_, index: number) => ({
                  label: `${index}`,
                  value: `${index}`,
                }))}
                value={lengthOfStayMonths}
                onChange={(evt: any) => onChange(VQ.LENGTH_IN_UK_INTENTION_MM, Number(evt.currentTarget.value))}
              />

              <Select
                key={VQ.LENGTH_IN_UK_INTENTION_YY}
                id={VQ.LENGTH_IN_UK_INTENTION_YY}
                label={t('common:years')}
                options={[...Array(100)].map((_, index: number) => ({
                  label: `${index}`,
                  value: `${index}`,
                }))}
                value={lengthOfStayYears}
                onChange={(evt: any) => onChange(VQ.LENGTH_IN_UK_INTENTION_YY, Number(evt.currentTarget.value))}
              />
            </Flex>
          </FormControl>
        </>
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.UK_PRESENCE}
        id={VQ.UK_PRESENCE}
        label={t('common:doYouHaveAnEstablishedPresenceIn')}
        onClick={(question: IQuestion) => onChange(VQ.UK_PRESENCE, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: ukPresence === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: ukPresence === false,
          },
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.UK_FAMILY}
        id={VQ.UK_FAMILY}
        label={t('common:doYouHaveFamilyInTheUk')}
        onClick={(question: IQuestion) => onChange(VQ.UK_FAMILY, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: ukFamily === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: ukFamily === false,
          },
        ]}
      />

      {ukFamily === true ? (
        <>
          <RadioOptionsWithConfirm
            key={VQ.STAYING_WITH_FAMILY}
            id={VQ.STAYING_WITH_FAMILY}
            label={t('common:willYouBeStayingWithAnyFamilyMem')}
            onClick={(question: IQuestion) => onChange(VQ.STAYING_WITH_FAMILY, question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: stayingWithFamily === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: stayingWithFamily === false,
              },
            ]}
          />

          {stayingWithFamily === true ? (
            <>
              <CardContainer title={t('common:howMany')}>
                {familyMembersState.map((details: IFamilyMember, index: number) => (
                  <CardWithModal
                    key={index}
                    id={`family-members-${index}`}
                    index={index}
                    modalTitle={'Add Family Member'}
                    cardItems={[
                      {
                        label: 'Nationality',
                        value: details.nationality,
                      },
                      {
                        label: 'Family name',
                        value: details.familyName,
                      },
                      {
                        label: 'Given name',
                        value: details.givenName,
                      },
                      {
                        label: 'Date of birth',
                        value: details.dob,
                      },
                    ]}
                    onRemove={deleteMember}
                    addButtonLabel={`Add Family Member`}
                    addAnotherButtonLabel={`I have another Family Member to add`}
                    hasAddMoreButton={
                      details.nationality === '' &&
                      details.familyName === '' &&
                      details.givenName === '' &&
                      details.dob === ''
                    }
                    showModal={showModal[index]}
                    toggleModal={toggleModal}
                  >
                    <FamilyMembers
                      index={index}
                      nationality={details.nationality}
                      familyName={details.familyName}
                      givenName={details.givenName}
                      dob={details.dob}
                      onSaveAndClose={(details: IFamilyMember) => {
                        const members = [...familyMembers];
                        members[index] = {
                          ...details,
                        };
                        onChange(VQ.FAMILY_MEMBERS, members);
                        toggleModal(index);
                      }}
                    />
                  </CardWithModal>
                ))}
              </CardContainer>

              <AddressInputWithConfirm
                key={VQ.FAMILY_CONTACT_ADDRESS}
                id={VQ.FAMILY_CONTACT_ADDRESS}
                addressLabel="Contact Address for Family you are staying with"
                addressSubText="(e.g. unit 33, 17 Example Street)"
                cityLabel="City"
                postcodeLabel="Postcode (if you have one)"
                countryLabel="Country"
                addressValue={familyContactAddress}
                cityValue={familyContactCity}
                postcodeValue={familyContactPostcode}
                countryValue={familyContactCountry}
                onChangeAddress={(event: any) => {
                  onChange(VQ.FAMILY_CONTACT_ADDRESS, event.currentTarget.value);
                }}
                onChangeCity={(event: any) => {
                  onChange(VQ.FAMILY_CONTACT_CITY, event.currentTarget.value);
                }}
                onChangePostcode={(event: any) => {
                  onChange(VQ.FAMILY_CONTACT_POSTCODE, event.currentTarget.value);
                }}
                onChangeCountry={(value: string) => {
                  onChange(VQ.FAMILY_CONTACT_COUNTRY, value);
                }}
              />

              <PhoneNumberInputWithConfirm
                key={VQ.FAMILY_CONTACT_NUMBER}
                id={VQ.FAMILY_CONTACT_NUMBER}
                value={familyPhone ? familyPhone : ''}
                label={t('common:contactNumber')}
                codeValue={familyPhone && familyPhone.code ? familyPhone.code : ''}
                numberValue={familyPhone && familyPhone.phone ? familyPhone.phone : ''}
                onValidationFailed={() => {
                  setFamilyPhoneValid(false);
                }}
                onValidationSuccess={() => {
                  const { code, phone } = familyPhone!;
                  onChange(VQ.FAMILY_CONTACT_NUMBER, `${code}-${phone}`);
                  setFamilyPhoneValid(true);
                }}
                onChangeCode={(event: React.FormEvent<HTMLInputElement>) => {
                  const code = event.currentTarget.value;
                  setFamilyPhone({
                    phone: familyPhone ? familyPhone.phone : '',
                    code,
                  });
                }}
                onChangeNumber={(event: React.FormEvent<HTMLInputElement>) => {
                  const { value } = event.currentTarget;
                  setFamilyPhone({
                    phone: value,
                    code: familyPhone ? familyPhone.code : '',
                  });
                }}
              />

              <InputWithConfirm
                key={VQ.FAMILY_CONTACT_EMAIL}
                id={VQ.FAMILY_CONTACT_EMAIL}
                value={familyEmailInput}
                label={t('common:contactEmailAddress')}
                onChange={(evt: React.FormEvent<HTMLInputElement>) => {
                  setFamilyEmailInput(evt.currentTarget.value);
                  if (isemail.validate(evt.currentTarget.value)) {
                    onChange(VQ.FAMILY_CONTACT_EMAIL, evt.currentTarget.value);
                    setFamilyEmailInputError('');
                  }
                }}
                onBlur={validateEmail}
                showErrorText={familyEmailInputError}
                errorText={familyEmailInputError}
              />
            </>
          ) : null}
        </>
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.HAS_OTHER_CONTACT_ADDRESS}
        id={VQ.HAS_OTHER_CONTACT_ADDRESS}
        label={t('common:doYouHaveAnotherAddressInTheUk')}
        onClick={(question: IQuestion) => onChange(VQ.HAS_OTHER_CONTACT_ADDRESS, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasOtherContactAddress === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasOtherContactAddress === false,
          },
        ]}
      />

      {hasOtherContactAddress === true ? (
        <>
          <AddressInputWithConfirm
            key={VQ.OTHER_CONTACT_ADDRESS}
            id={VQ.OTHER_CONTACT_ADDRESS}
            addressLabel="UK Contact Address"
            addressSubText="(e.g. unit 33, 17 Example Street)"
            cityLabel="City"
            postcodeLabel="Postcode (if you have one)"
            countryLabel="Country"
            addressValue={otherContactAddress}
            cityValue={otherContactCity}
            postcodeValue={otherContactPostcode}
            countryValue={otherContactCountry}
            onChangeAddress={(event: any) => {
              onChange(VQ.OTHER_CONTACT_ADDRESS, event.currentTarget.value);
            }}
            onChangeCity={(event: any) => {
              onChange(VQ.OTHER_CONTACT_CITY, event.currentTarget.value);
            }}
            onChangePostcode={(event: any) => {
              onChange(VQ.OTHER_CONTACT_POSTCODE, event.currentTarget.value);
            }}
            onChangeCountry={(value: string) => {
              onChange(VQ.OTHER_CONTACT_COUNTRY, value);
            }}
          />

          <PhoneNumberInputWithConfirm
            key={VQ.OTHER_CONTACT_NUMBER}
            id={VQ.OTHER_CONTACT_NUMBER}
            value={otherPhone ? otherPhone : ''}
            label={t('common:contactNumber')}
            codeValue={otherPhone && otherPhone.code ? otherPhone.code : ''}
            numberValue={otherPhone && otherPhone.phone ? otherPhone.phone : ''}
            onValidationFailed={() => {
              setOtherPhoneValid(false);
            }}
            onValidationSuccess={() => {
              const { code, phone } = otherPhone!;
              onChange(VQ.OTHER_CONTACT_NUMBER, `${code}-${phone}`);
              setOtherPhoneValid(true);
            }}
            onChangeCode={(event: React.FormEvent<HTMLInputElement>) => {
              const code = event.currentTarget.value;
              setOtherPhone({
                phone: otherPhone ? otherPhone.phone : '',
                code,
              });
            }}
            onChangeNumber={(event: React.FormEvent<HTMLInputElement>) => {
              const { value } = event.currentTarget;
              setOtherPhone({
                phone: value,
                code: otherPhone ? otherPhone.code : '',
              });
            }}
          />
        </>
      ) : null}
    </QuestionPage>
  );
};

export default VisaPrepFormArrivalPage1;
